import React, { useEffect, useState } from "react";
import { getBusinesses, submitBusiness } from "../lib/api";
import LoyaltyBadge from "../components/LoyaltyBadge";
import { toast } from "sonner";
import { MapPin, Phone, ExternalLink, ShieldCheck, Star, Plus, X } from "lucide-react";

const CATS = ["All", "Dining", "Services", "Retail", "Lodging"];

export default function Directory() {
  const [businesses, setBusinesses] = useState([]);
  const [filter, setFilter] = useState("All");
  const [showForm, setShowForm] = useState(false);

  const load = (cat) => getBusinesses(cat === "All" ? undefined : cat).then(setBusinesses).catch(() => {});

  useEffect(() => { load(filter); }, [filter]);

  return (
    <div className="paper-texture min-h-screen">
      <div className="border-b border-[var(--paper-border)] bg-[var(--navy)]">
        <div className="mx-auto max-w-7xl px-4 py-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow text-[var(--gold)]">Always Free to Browse</p>
              <h1 className="font-display text-4xl font-extrabold text-white sm:text-5xl">Local Business Directory</h1>
              <p className="font-serif-body mt-2 max-w-xl text-white/80">
                Discover the businesses that make Amelia Island and Nassau County special — from clients to community favorites.
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              data-testid="list-business-btn"
              className="font-ui flex items-center gap-2 rounded-full bg-[var(--gold)] px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#b08f48]"
            >
              <Plus size={16} /> List Your Business
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="mb-8 flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`directory-filter-${c.toLowerCase()}`}
              className={`font-ui rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                filter === c ? "border-[var(--navy)] bg-[var(--navy)] text-white" : "border-[var(--paper-border)] bg-white text-slate-600 hover:border-[var(--navy)]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {businesses.map((b) => (
            <div
              key={b.id}
              data-testid={`business-${b.id}`}
              className={`card-hover flex flex-col overflow-hidden rounded-lg border bg-white ${
                b.sponsored ? "border-[var(--gold)] ring-1 ring-[var(--gold)]/30" : "border-[var(--paper-border)]"
              }`}
            >
              {b.image && (
                <div className="img-zoom relative h-40 overflow-hidden">
                  <img src={b.image} alt={b.name} className="h-full w-full object-cover" />
                  {b.loyalty && <LoyaltyBadge className="absolute right-3 top-3" />}
                  {b.sponsored && (
                    <span className="eyebrow absolute left-3 top-3 flex items-center gap-1 rounded-full bg-[var(--gold)] px-2 py-1 text-[9px] text-white">
                      <Star size={10} /> Sponsored
                    </span>
                  )}
                </div>
              )}
              <div className="flex flex-1 flex-col p-4">
                <div className="flex items-center gap-2">
                  <span className="eyebrow text-[var(--gold)]">{b.category}</span>
                  {b.loyalty && !b.image && <LoyaltyBadge />}
                  {b.tier === "premium" && (
                    <span className="eyebrow flex items-center gap-1 rounded-sm bg-blue-50 px-1.5 py-0.5 text-[9px] text-blue-700">
                      <ShieldCheck size={10} /> Premium Verified
                    </span>
                  )}
                </div>
                <h3 className="font-display mt-1 text-xl font-bold text-[var(--ink)]">{b.name}</h3>
                <p className="font-serif-body mt-1 flex-1 text-sm text-slate-600">{b.description}</p>
                <div className="font-ui mt-3 space-y-1 text-xs text-slate-500">
                  {b.address && <p className="flex items-center gap-1.5"><MapPin size={12} className="text-[var(--gold)]" /> {b.address}</p>}
                  {b.phone && <p className="flex items-center gap-1.5"><Phone size={12} className="text-[var(--gold)]" /> {b.phone}</p>}
                  {b.website && <p className="flex items-center gap-1.5"><ExternalLink size={12} className="text-[var(--gold)]" /> {b.website}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Sponsorship CTA */}
        <div className="mt-12 grid gap-6 rounded-xl border border-[var(--paper-border)] bg-white p-8 md:grid-cols-3">
          <div className="md:col-span-2">
            <h3 className="font-display text-2xl font-bold text-[var(--navy)]">Premium & Sponsored listings</h3>
            <p className="font-serif-body mt-2 text-slate-600">
              Rise to the top of the directory, add photos and a verified badge, and reach 40,000+ engaged island readers each week. Non-clients are listed free — clients get the spotlight.
            </p>
          </div>
          <div className="flex items-center justify-center">
            <button onClick={() => setShowForm(true)} className="font-ui rounded-full bg-[var(--navy)] px-6 py-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[var(--header-navy)]">
              Become a Sponsor
            </button>
          </div>
        </div>
      </div>

      {showForm && <ListingForm onClose={() => setShowForm(false)} />}
    </div>
  );
}

function ListingForm({ onClose }) {
  const [form, setForm] = useState({ name: "", category: "Dining", phone: "", address: "", website: "", description: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Business name is required.");
    setBusy(true);
    try {
      const res = await submitBusiness(form);
      toast.success(res.message);
      onClose();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="listing-form">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-2xl font-bold text-[var(--navy)]">List Your Business</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Business Name *"><input data-testid="biz-name" value={form.name} onChange={set("name")} className="nl-input" /></Field>
          <Field label="Category">
            <select data-testid="biz-category" value={form.category} onChange={set("category")} className="nl-input">
              {CATS.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><input data-testid="biz-phone" value={form.phone} onChange={set("phone")} className="nl-input" /></Field>
            <Field label="Website"><input data-testid="biz-website" value={form.website} onChange={set("website")} className="nl-input" /></Field>
          </div>
          <Field label="Address"><input data-testid="biz-address" value={form.address} onChange={set("address")} className="nl-input" /></Field>
          <Field label="Description"><textarea data-testid="biz-description" value={form.description} onChange={set("description")} rows={3} className="nl-input" /></Field>
          <button type="submit" disabled={busy} data-testid="biz-submit" className="font-ui w-full rounded-full bg-[var(--gold)] px-5 py-3 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#b08f48] disabled:opacity-60">
            {busy ? "Submitting…" : "Submit Listing"}
          </button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-ui mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    {children}
  </label>
);
